/**
 * PATH: packages/ui/src/components/settings/settings-tab-sections/admin-sections.tsx
 * PURPOSE: Build admin-only tab sections (Organisation hub, Team, Access Control)
 */

import {
  Building2,
  Paintbrush,
  Users,
  Boxes,
  UserCog,
  ShieldCheck,
} from 'lucide-react'
import { OrganisationHubTab } from '../OrganisationHubTab'
import { EntitiesTab, type EntitiesTabProps } from '../EntitiesTab'
import { BrandingTab, type BrandingTabProps } from '../BrandingTab'
import { TeamTab, type TeamTabProps } from '../TeamTab'
import { AccessControlTab, type AccessControlTabProps } from '../AccessControlTab'
import { ModuleAccessTab, type ModuleAccessTabProps } from '../ModuleAccessTab'
import { RoleManagementTab, type RoleManagementTabProps } from '../RoleManagementTab'
import type { SettingsSection } from '../SectionedContent'
import type { SettingsTab } from '../../SettingsModal'
import type { BuildSettingsTabsOptions } from '../buildSettingsTabs'
// =============================================================================
// ORGANISATION HUB (Details + Entities + Integrations — one sidebar entry)
// =============================================================================

export function buildOrganisationHubTab(
  options: BuildSettingsTabsOptions,
): SettingsTab | null {
  const {
    apiClient,
    organisationId,
    organisationData,
    onOrganisationSave,
    organisationContent,
    entitiesProps,
    organisationInitialPanel,
    integrations,
    onIntegrationConnect,
    onIntegrationDisconnect,
    onIntegrationSync,
    integrationsContent,
    isIntegrationsLoading,
    onOpenIntegrationsTab,
    user,
  } = options

  // OrganisationSettings.enable_xero — false only when an admin has marked
  // Xero "not applicable" to this org. Gates the Entities tab's Xero
  // tenant-mapping coupling (the operator's complaint: Xero + Entities share
  // one tab, but some orgs use QuickBooks / MYOB / no accounting). We do NOT
  // hide the Xero card from the Integrations panel — that panel is the
  // connect / re-enable surface, and removing it would be a one-way door
  // (the backend only re-enables enable_xero via an explicit settings PATCH,
  // never on a Xero OAuth connect).
  const xeroApplicable = options.xeroApplicable !== false

  const resolvedEntitiesProps: EntitiesTabProps = {
    apiClient,
    organisationId,
    isXeroConnected: options.isXeroConnected,
    xeroApplicable,
    onConnectXero: onOpenIntegrationsTab,
    ...entitiesProps,
  }

  return {
    id: 'organisation',
    label: 'Organisation & Integrations',
    icon: Building2,
    category: 'Organisation',
    description: 'Company details, legal entities, and connected services',
    content: (
      <OrganisationHubTab
        initialPanel={organisationInitialPanel ?? 'details'}
        organisation={organisationData}
        onOrganisationSave={onOrganisationSave}
        organisationContent={organisationContent}
        entitiesProps={resolvedEntitiesProps}
        integrations={integrations}
        isIntegrationsLoading={isIntegrationsLoading}
        onIntegrationConnect={onIntegrationConnect}
        onIntegrationDisconnect={onIntegrationDisconnect}
        onIntegrationSync={onIntegrationSync}
        integrationsContent={integrationsContent}
        apiClient={apiClient}
        organisationId={organisationId}
        user={user}
      />
    ),
  }
}

/**
 * buildBrandingSection — logo, colors, document templates (standalone sidebar tab)
 */
export function buildBrandingSection(
  options: BuildSettingsTabsOptions,
): SettingsSection[] {
  const resolvedBrandingProps: BrandingTabProps = {
    apiClient: options.apiClient,
    ...options.brandingProps,
  }

  return [
    {
      id: 'branding',
      title: 'Branding',
      icon: Paintbrush,
      description: 'Logo, colors, and document templates',
      content: <BrandingTab {...resolvedBrandingProps} />,
    },
  ]
}

/** @deprecated Use buildOrganisationHubTab — kept for tests importing buildOrgSections */
export function buildOrgSections(options: BuildSettingsTabsOptions): SettingsSection[] {
  return buildBrandingSection(options)
}

// =============================================================================
// TEAM SECTIONS
// =============================================================================

export function buildTeamSections(
  options: BuildSettingsTabsOptions,
): SettingsSection[] {
  const {
    user,
    apiClient,
    organisationId,
    teamProps,
  } = options

  const resolvedTeamProps: TeamTabProps = {
    apiClient,
    organisationId,
    isAdmin: user?.isAdmin || false,
    currentUserId: user?.id ? Number(user.id) : undefined,
    ...teamProps,
  }

  return [
    {
      id: 'team-members',
      title: 'Team Members',
      icon: Users,
      description: 'Users, invitations, and roles',
      content: <TeamTab {...resolvedTeamProps} />,
    },
  ]
}

// =============================================================================
// ACCESS CONTROL SECTIONS
// =============================================================================

export function buildAccessSections(
  options: BuildSettingsTabsOptions,
): SettingsSection[] {
  const {
    user,
    apiClient,
    organisationId,
    accessControlProps,
    moduleAccessProps,
    roleManagementProps,
  } = options

  const resolvedAccessControlProps: AccessControlTabProps = {
    apiClient,
    organisationId,
    user: user || undefined,
    ...accessControlProps,
  }

  const resolvedModuleAccessProps: ModuleAccessTabProps = {
    apiClient,
    organisationId,
    ...moduleAccessProps,
  }

  const resolvedRoleManagementProps: RoleManagementTabProps = {
    apiClient,
    organisationId,
    user: user || undefined,
    ...roleManagementProps,
  }

  return [
    {
      id: 'roles',
      title: 'Roles & Permissions',
      icon: UserCog,
      description: 'Define roles and assign to members',
      content: <RoleManagementTab {...resolvedRoleManagementProps} />,
    },
    {
      id: 'module-access',
      title: 'Module Access',
      icon: Boxes,
      description: 'Enable or disable hubs per organisation',
      content: <ModuleAccessTab {...resolvedModuleAccessProps} />,
      defaultCollapsed: true,
    },
    {
      id: 'page-permissions',
      title: 'Page Permissions',
      icon: ShieldCheck,
      description: 'Fine-grained page-level access rules',
      content: <AccessControlTab {...resolvedAccessControlProps} />,
      defaultCollapsed: true,
    },
  ]
}
